import { createStyles, Select, type SelectProps } from '@mantine/core';
import { useMediaQuery } from '@mantine/hooks';
import { useState } from 'react';
import { ASSETS_BUCKET_URL } from '@/config';

const useStyles = createStyles((theme, { floating }: { floating: boolean }) => ({
  root: {
    position: 'relative',
    width: '100%',
  },

  label: {
    position: 'absolute',
    zIndex: 2,
    top: floating ? 22 : 18,
    left: theme.spacing.md,
    pointerEvents: 'none',
    // eslint-disable-next-line no-nested-ternary
    color: floating
      ? theme.colorScheme === 'dark'
        ? theme.white
        : theme.colors.gray[6]
      : theme.colorScheme === 'dark'
      ? theme.colors.dark[3]
      : theme.colors.gray[5],
    transition: 'transform 150ms ease, color 150ms ease, font-size 150ms ease',
    transform: floating ? 'translate(0, -12px)' : 'none',
    fontSize: floating ? theme.fontSizes.md : theme.fontSizes.lg,
    fontWeight: floating ? 400 : 600,
    lineHeight: 1.13,
    letterSpacing: '0.19px',
  },

  required: {
    transition: 'opacity 150ms ease',
    opacity: floating ? 1 : 0,
  },

  input: {
    border: 'none',
    backgroundColor: '#f7f7f7',
    outline: 'none',
    height: 54,
    fontSize: '16px',
    fontWeight: 600,
    lineHeight: 1.13,
    letterSpacing: '0.19px',
    borderRadius: '8px',
    color: '#212121 !important',
    paddingTop: '1.25rem',
    paddingLeft: theme.spacing.md,

    '&::placeholder': {
      transition: 'color 150ms ease',
      color: !floating ? 'transparent' : undefined,
    },
  },
  rightSection: {
    right: theme.spacing.sm,
  },
}));

type SelectRegister = {
  value: string;
  onChange: (value: string | null) => void;
  onBlur?: () => void;
  onFocus?: () => void;
  error?: string | undefined;
};

type SelectFieldProps = SelectProps & {
  label: string;
  data: any;
  register: SelectRegister;
};

export const SelectField = (props: SelectFieldProps) => {
  const { label, data, register } = props;

  const [focused, setFocused] = useState(false);
  const isMobile = useMediaQuery('(max-width: 768px)', true);

  const { value, error, onChange, onBlur, onFocus } = register;
  const { classes } = useStyles({ floating: value?.toString().length !== 0 || focused });

  return (
    <Select
      {...props}
      label={label}
      data={data}
      classNames={classes}
      styles={(theme) => ({
        item: {
          '&[data-selected]': {
            '&, &:hover': {
              backgroundColor: '#FFFFFF',
              color: theme.colors.dark[6],
            },
          },
          fontSize: '16px',
        },
        rightSection: { pointerEvents: 'none' },
        dropdown: {
          borderRadius: isMobile ? 8 : 12,
          borderColor: '#ede7fa',
          boxShadow: '0px 10px 20px rgba(167, 167, 167, 0.16)',
        },
        separatorLabel: {},
      })}
      onChange={(e) => {
        onChange(e);
        setFocused(false);
      }}
      onBlur={() => {
        setFocused(false);
        onBlur?.();
      }}
      onFocus={() => {
        setFocused(true);
        onFocus?.();
      }}
      onDropdownClose={() => {
        setFocused(false);
      }}
      onDropdownOpen={() => {
        setFocused(true);
        onFocus?.();
      }}
      error={error}
      value={value}
      transitionProps={{
        transition: 'pop-top-left',
        duration: 80,
        timingFunction: 'ease',
      }}
      withinPortal
      rightSection={
        focused ? (
          <img
            src={`${ASSETS_BUCKET_URL}/svg/UpArrow.svg`}
            alt="UpArrow"
            loading="lazy"
            className="pointer"
            onClick={() => {
              setFocused(false);
              onBlur?.();
            }}
          />
        ) : (
          <img
            src={`${ASSETS_BUCKET_URL}/svg/DownArrow.svg`}
            alt="DownArrow"
            loading="lazy"
            className="pointer"
            onClick={() => {
              onFocus?.();
            }}
          />
        )
      }
    />
  );
};
